// Pixel-rendered phone overlay launched from the dock.
// Intentional dissonance: the drone in the pixel house pulls out a chunky pixel phone.
// Pulls APPS from world.config.js. Recursive ian-world tap shows a flavor message.
// Real apps load icon textures via the live Phaser game (window.__iwGame__) — already loaded
// by BootScene — so the icons stay consistent with what the game itself uses.

import { APPS } from '../world.config.js';

export class PhoneSystem {
  constructor() {
    this.scrim = null;
    this.frame = null;
    this.screen = null;
    this.flashEl = null;
    this.isOpen = false;
    this.onClose = null;
    this._keyHandler = null;
    this._mount();
  }

  _mount() {
    const existing = document.querySelector('.iw-phone-scrim');
    if (existing) {
      this.scrim = existing;
      this.frame = existing.querySelector('.iw-phone-frame');
      this.screen = existing.querySelector('.iw-phone-screen');
      this.flashEl = existing.querySelector('.iw-phone-flash');
      this.scrim.style.display = 'none';
      return;
    }

    const scrim = document.createElement('div');
    scrim.className = 'iw-phone-scrim';
    scrim.style.cssText = `
      position: fixed;
      inset: 0;
      background: rgba(8, 6, 5, 0.78);
      backdrop-filter: blur(3px);
      display: none;
      align-items: center;
      justify-content: center;
      z-index: 70;
      pointer-events: auto;
    `;

    // Phone frame — chunky pixel device (intentional dissonance)
    const frame = document.createElement('div');
    frame.className = 'iw-phone-frame';
    frame.style.cssText = `
      width: 280px;
      height: 540px;
      background: #14110e;
      border: 6px solid #2a2018;
      border-radius: 28px;
      padding: 14px 12px 18px;
      box-shadow:
        inset 0 0 0 2px #3a2c20,
        0 24px 60px rgba(0, 0, 0, 0.65),
        0 0 0 1px rgba(244, 234, 213, 0.06);
      display: flex;
      flex-direction: column;
      gap: 10px;
      image-rendering: pixelated;
      font-family: ui-monospace, "SF Mono", Menlo, monospace;
      position: relative;
    `;

    // Speaker grille (decorative, top of phone)
    const speaker = document.createElement('div');
    speaker.style.cssText = `
      width: 40px;
      height: 4px;
      background: #2a2018;
      border-radius: 2px;
      margin: 0 auto;
    `;

    // Screen — cream background, status bar, app grid, home pill
    const screen = document.createElement('div');
    screen.className = 'iw-phone-screen';
    screen.style.cssText = `
      flex: 1;
      background: #F3E5C2;
      border-radius: 4px;
      padding: 10px 12px;
      display: flex;
      flex-direction: column;
      gap: 12px;
      color: #14110e;
      overflow: hidden;
      position: relative;
    `;

    // Status bar (top of screen)
    const statusBar = document.createElement('div');
    statusBar.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 9px;
      letter-spacing: 0.06em;
      color: #6b4f3a;
      text-transform: uppercase;
    `;
    const time = document.createElement('span');
    time.textContent = formatTime();
    const status = document.createElement('span');
    status.textContent = '●●●●  ☼  88%';

    // Right side of status bar groups the status indicators with the close
    // button so the X can't collide with the battery readout.
    const statusRight = document.createElement('div');
    statusRight.style.cssText = `
      display: flex;
      align-items: center;
      gap: 8px;
    `;

    // X close button — sits inline at the far right of the status bar
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.textContent = '×';
    closeBtn.style.cssText = `
      background: transparent;
      border: none;
      color: #754C60;
      font-size: 16px;
      line-height: 1;
      cursor: pointer;
      padding: 0 2px;
      opacity: 0.6;
      font-family: inherit;
      transition: opacity 140ms ease;
    `;

    statusRight.appendChild(status);
    statusRight.appendChild(closeBtn);
    statusBar.appendChild(time);
    statusBar.appendChild(statusRight);
    closeBtn.addEventListener('mouseenter', () => { closeBtn.style.opacity = '1'; });
    closeBtn.addEventListener('mouseleave', () => { closeBtn.style.opacity = '0.6'; });
    closeBtn.addEventListener('click', (event) => {
      event.stopPropagation();
      this.close();
    });

    // App grid (3 apps, evenly spaced in a 3-column grid)
    const grid = document.createElement('div');
    grid.className = 'iw-phone-grid';
    grid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 14px;
      padding: 6px 0;
    `;

    // Page title
    const pageTitle = document.createElement('div');
    pageTitle.style.cssText = `
      font-size: 10px;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: #754C60;
      text-align: center;
      opacity: 0.7;
    `;
    pageTitle.textContent = 'home';

    // Home indicator pill (bottom of screen)
    const homeIndicator = document.createElement('div');
    homeIndicator.style.cssText = `
      width: 60px;
      height: 4px;
      background: #754C60;
      border-radius: 2px;
      margin: auto auto 0;
      opacity: 0.45;
    `;

    // Tap hint
    const tapHint = document.createElement('div');
    tapHint.style.cssText = `
      font-size: 9px;
      color: #754C60;
      text-align: center;
      opacity: 0.55;
      letter-spacing: 0.08em;
    `;
    tapHint.textContent = 'tap an app · esc to close';

    // Recursive flash overlay — covers the screen briefly when ian-world is tapped
    const flash = document.createElement('div');
    flash.className = 'iw-phone-flash';
    flash.style.cssText = `
      position: absolute;
      inset: 14px 12px 18px;
      border-radius: 4px;
      background: #14110e;
      color: #f4ead5;
      display: none;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 16px;
      padding: 20px;
      text-align: center;
      font-family: ui-monospace, "SF Mono", Menlo, monospace;
    `;

    screen.appendChild(statusBar);
    screen.appendChild(pageTitle);
    screen.appendChild(grid);
    screen.appendChild(tapHint);
    screen.appendChild(homeIndicator);

    frame.appendChild(speaker);
    frame.appendChild(screen);
    frame.appendChild(flash);

    scrim.appendChild(frame);
    document.body.appendChild(scrim);

    // Click outside the frame closes
    scrim.addEventListener('click', (event) => {
      if (event.target === scrim) this.close();
    });

    this.scrim = scrim;
    this.frame = frame;
    this.screen = screen;
    this.gridEl = grid;
    this.flashEl = flash;
    this._renderApps();
  }

  _renderApps() {
    if (!this.gridEl) {
      this.gridEl = this.screen.querySelector('.iw-phone-grid');
    }
    this.gridEl.innerHTML = '';
    for (const app of APPS) {
      const tile = document.createElement('button');
      tile.type = 'button';
      tile.style.cssText = `
        background: transparent;
        border: 0;
        padding: 0;
        cursor: pointer;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;
        font-family: ui-monospace, "SF Mono", Menlo, monospace;
        color: #14110e;
      `;

      const iconWrap = document.createElement('div');
      iconWrap.style.cssText = `
        width: 56px;
        height: 56px;
        border-radius: 12px;
        background: ${app.accent ?? '#754C60'};
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        box-shadow:
          inset 0 -2px 0 rgba(0, 0, 0, 0.18),
          inset 0 2px 0 rgba(255, 255, 255, 0.15),
          0 2px 0 rgba(0, 0, 0, 0.25);
        transition: transform 100ms ease;
        image-rendering: pixelated;
      `;

      const img = document.createElement('img');
      img.src = textureToDataURL(app.textureKey);
      img.alt = app.label;
      const iconPx = Math.round(48 * (app.iconScale ?? 1));
      img.style.cssText = `
        width: ${iconPx}px;
        height: ${iconPx}px;
        image-rendering: pixelated;
      `;
      iconWrap.appendChild(img);

      const label = document.createElement('span');
      label.textContent = app.label;
      label.style.cssText = `
        font-size: 9px;
        letter-spacing: 0.04em;
        color: #14110e;
        max-width: 70px;
        text-align: center;
        line-height: 1.2;
      `;

      tile.appendChild(iconWrap);
      tile.appendChild(label);

      tile.addEventListener('mousedown', () => { iconWrap.style.transform = 'scale(0.92)'; });
      tile.addEventListener('mouseup', () => { iconWrap.style.transform = 'scale(1)'; });
      tile.addEventListener('mouseleave', () => { iconWrap.style.transform = 'scale(1)'; });

      tile.addEventListener('click', () => this._handleAppTap(app));

      this.gridEl.appendChild(tile);
    }
  }

  _handleAppTap(app) {
    if (app.kind === 'recursive') {
      this._showRecursiveFlash();
      return;
    }
    if (app.url) {
      window.open(app.url, '_blank', 'noopener,noreferrer');
    }
  }

  _showRecursiveFlash() {
    if (!this.flashEl) return;
    this.flashEl.innerHTML = '';
    const title = document.createElement('div');
    title.textContent = "you're already here";
    title.style.cssText = `
      font-size: 14px;
      letter-spacing: 0.12em;
      text-transform: lowercase;
      color: #f6b93b;
    `;
    const sub = document.createElement('div');
    sub.textContent = "look up. the house. that's the app.";
    sub.style.cssText = `
      font-size: 11px;
      color: rgba(244, 234, 213, 0.7);
      line-height: 1.5;
      max-width: 200px;
    `;
    const wink = document.createElement('div');
    wink.textContent = '— ian';
    wink.style.cssText = `
      font-size: 10px;
      color: rgba(244, 234, 213, 0.4);
      letter-spacing: 0.08em;
    `;
    this.flashEl.appendChild(title);
    this.flashEl.appendChild(sub);
    this.flashEl.appendChild(wink);
    this.flashEl.style.display = 'flex';
    setTimeout(() => {
      if (this.flashEl) this.flashEl.style.display = 'none';
    }, 1900);
  }

  open({ onClose } = {}) {
    this.onClose = onClose ?? null;
    this.isOpen = true;
    this.scrim.style.display = 'flex';
    // Refresh icons on open in case textures became available after first mount
    this._renderApps();
    this._bindKeys();
  }

  _bindKeys() {
    if (this._keyHandler) return;
    this._keyHandler = (event) => {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    };
    window.addEventListener('keydown', this._keyHandler);
  }

  _unbindKeys() {
    if (this._keyHandler) {
      window.removeEventListener('keydown', this._keyHandler);
      this._keyHandler = null;
    }
  }

  close() {
    if (!this.isOpen) return;
    this._unbindKeys();
    this.isOpen = false;
    this.scrim.style.display = 'none';
    if (this.flashEl) this.flashEl.style.display = 'none';
    const cb = this.onClose;
    this.onClose = null;
    cb?.();
  }
}

function formatTime() {
  const d = new Date();
  let h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, '0');
  const ampm = h >= 12 ? 'pm' : 'am';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${ampm}`;
}

// Pull a Phaser texture's underlying canvas/image and return a data URL we can <img src=…>
function textureToDataURL(textureKey) {
  const game = window.__iwGame__;
  if (!game) return transparentPixel();
  const tex = game.textures.get(textureKey);
  if (!tex || tex.key === '__MISSING') return transparentPixel();
  try {
    const src = tex.getSourceImage();
    // CanvasTexture path
    if (src instanceof HTMLCanvasElement) {
      return src.toDataURL();
    }
    // HTMLImageElement path — re-render to a canvas to get a data URL
    const c = document.createElement('canvas');
    c.width = src.width;
    c.height = src.height;
    const ctx = c.getContext('2d');
    if (ctx && src.width && src.height) {
      ctx.drawImage(src, 0, 0);
      return c.toDataURL();
    }
  } catch (_) {}
  return transparentPixel();
}

function transparentPixel() {
  return 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
}
